import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Globe, Instagram, MapPin, Linkedin, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation, languages, Language } from '@/lib/i18n';
import zeynLogo from '@/assets/zeyn-tour-logo.png';

export function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation(language);
  const location = useLocation();

  const navLinks = [
    { key: 'home', path: '/' },
    { key: 'cars', path: '/cars' },
    { key: 'services', path: '/services' },
    { key: 'campaigns', path: '/campaigns' },
    { key: 'about', path: '/about' },
    { key: 'contact', path: '/contact' }
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav className="bg-background/95 backdrop-blur-md border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
          <div className="flex items-center gap-2">
              <Link 
                to="/" 
                className="flex items-center space-x-2"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <img src={zeynLogo} alt="Zeyn Tour" className="h-12 md:h-14 lg:h-16 w-auto" width="64" height="64" fetchPriority="high" />
              </Link>
            </div>

            {/* Desktop Navigation (hidden on tablet) */}
            <div className={`hidden lg:flex items-center ${language === 'ar' ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
              {navLinks.map((link) => (
                <Link
                  key={link.key}
                  to={link.path}
                  className={`transition-colors hover:text-primary whitespace-nowrap ${
                    isActive(link.path) ? 'text-primary font-medium' : 'text-foreground'
                  }`}
                >
                  {t(`nav.${link.key}`)}
                </Link>
              ))}
            </div>

            {/* Language Selector & Mobile Menu */}
            <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center space-x-1" aria-label="Select language">
                    <Globe className="h-4 w-4" />
                    <span className="hidden sm:inline">{languages[language]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {Object.entries(languages).map(([lang, name]) => (
                    <DropdownMenuItem
                      key={lang}
                      onClick={() => setLanguage(lang as Language)}
                      className={language === lang ? 'bg-primary/10' : ''}
                    >
                      {name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile & Tablet Menu Sheet */}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="lg:hidden"
                    aria-label="Open navigation menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side={language === 'ar' ? 'left' : 'right'} className="w-[300px] sm:w-[350px] flex flex-col">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <img src={zeynLogo} alt="Zeyn Tour" className="h-12 w-auto" width="48" height="48" loading="lazy" />
                    </SheetTitle>
                  </SheetHeader>
                  
                  {/* Navigation Links */}
                  <nav className="flex flex-col gap-2 mt-8 flex-1">
                    {navLinks.map((link) => (
                      <Link
                        key={link.key}
                        to={link.path}
                        className={`px-4 py-3 rounded-lg transition-colors text-lg ${
                          isActive(link.path) 
                            ? 'bg-primary text-primary-foreground font-medium' 
                            : 'hover:bg-muted text-foreground'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t(`nav.${link.key}`)}
                      </Link>
                    ))}
                  </nav>

                  {/* Social Icons at Bottom */}
                  <div className="border-t pt-6 pb-4">
                    <p className="text-sm text-muted-foreground mb-4 px-4">
                      {t('contact.followUs')}
                    </p>
                    <div className="flex gap-3 px-4 overflow-x-auto pb-2">
                      <a 
                        href="https://www.instagram.com/zeyntour1/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-primary/10 hover:bg-primary/20 p-3 rounded-full transition-colors flex-shrink-0"
                        aria-label="Instagram"
                      >
                        <Instagram className="h-5 w-5 text-primary" />
                      </a>
                      <a 
                        href="https://www.facebook.com/profile.php?id=61557242953001" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-primary/10 hover:bg-primary/20 p-3 rounded-full transition-colors flex-shrink-0"
                        aria-label="Facebook"
                      >
                        <Facebook className="h-5 w-5 text-primary" />
                      </a>
                      <a 
                        href="https://www.linkedin.com/company/zeyntour/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-primary/10 hover:bg-primary/20 p-3 rounded-full transition-colors flex-shrink-0"
                        aria-label="LinkedIn"
                      >
                        <Linkedin className="h-5 w-5 text-primary" />
                      </a>
                      <a 
                        href="https://www.tiktok.com/@zeyntour1" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-primary/10 hover:bg-primary/20 p-3 rounded-full transition-colors flex-shrink-0"
                        aria-label="TikTok"
                      >
                        <svg className="h-5 w-5 text-primary" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                        </svg>
                      </a>
                      <a 
                        href="https://wa.me/905051647998" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-primary/10 hover:bg-primary/20 p-3 rounded-full transition-colors flex-shrink-0"
                        aria-label="WhatsApp"
                      >
                        <svg className="h-5 w-5 text-primary" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </a>
                      <a 
                        href="https://maps.app.goo.gl/Sa1mWLUJELyuM4wb9" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-primary/10 hover:bg-primary/20 p-3 rounded-full transition-colors flex-shrink-0"
                        aria-label="Location"
                      >
                        <MapPin className="h-5 w-5 text-primary" />
                      </a>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}